import merge from 'lodash.merge';

// Правила форм числительных для createI18n({ pluralRules: i18nManager.pluralRules }).
// Русский выбирает форму по трём веткам строки ('{n} день | {n} дня | {n} дней');
// у английского работает правило vue-i18n по умолчанию (две ветки). Форму
// выбирает библиотека — своих функций выбора формы в проектах быть не должно.
const pluralRules = {
  ru: (choice) => {
    const n = Math.abs(choice);
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 0;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1;
    return 2;
  },
};

class I18nManager {
  constructor() {
    this.moduleLocales = new Map();
    this.overrides = new Map();
    this.extensions = new Map();
    this.i18nInstance = null;
    this.supportedLocales = null;
    this.localeNames = null;
    this.defaultLocale = null;
    this.pluralRules = pluralRules;
  }

  setSupportedLocales(locales) {
    if (Array.isArray(locales)) {
      this.supportedLocales = locales;
      this.localeNames = null;
    } else {
      // Объект { код: подпись } — подписи показывает LocaleSwitcher.
      this.supportedLocales = Object.keys(locales);
      this.localeNames = locales;
    }
    return this;
  }

  // Язык без префикса в URL. Читают роут-гард setLocale (middlewares/localization.js)
  // и SEO-блок App.vue (canonical/hreflang).
  setDefaultLocale(locale) {
    this.defaultLocale = locale;
    return this;
  }

  setI18nInstance(i18n) {
    this.i18nInstance = i18n;
  }

  register(moduleName, locales) {
    if (typeof moduleName !== 'string' || !this.isValidLocales(locales)) {
      throw new TypeError('Invalid arguments for register()');
    }
    this.moduleLocales.set(moduleName, locales);

    // Если i18n instance уже создан, добавляем переводы динамически
    // и переприменяем extensions/overrides чтобы сохранить приоритет слоёв
    if (this.i18nInstance) {
      const localeKeys = Object.keys(locales);
      const ext = this.extensions.get(moduleName);
      const ovr = this.overrides.get(moduleName);

      localeKeys.forEach(locale => {
        if (locales[locale]) this.i18nInstance.global.mergeLocaleMessage(locale, locales[locale]);
        if (ext?.[locale]) this.i18nInstance.global.mergeLocaleMessage(locale, ext[locale]);
        if (ovr?.[locale]) this.i18nInstance.global.mergeLocaleMessage(locale, ovr[locale]);
      });
    }

    return this;
  }

  extend(moduleName, locales) {
    if (!this.isValidLocales(locales)) throw new TypeError('Invalid locales for extend()');
    const existing = this.extensions.get(moduleName) || {};
    merge(existing, locales);
    this.extensions.set(moduleName, existing);
    return this;
  }

  override(moduleName, locales) {
    if (!this.isValidLocales(locales)) throw new TypeError('Invalid locales for override()');
    const existing = this.overrides.get(moduleName) || {};
    merge(existing, locales);
    this.overrides.set(moduleName, existing);
    return this;
  }

  getMessages(locale) {
    const result = {};

    for (const [, moduleLocales] of this.moduleLocales) {
      if (moduleLocales[locale]) {
        merge(result, moduleLocales[locale]);
      }
    }

    for (const [, ext] of this.extensions) {
      if (ext[locale]) {
        merge(result, ext[locale]);
      }
    }

    for (const [, override] of this.overrides) {
      if (override[locale]) {
        merge(result, override[locale]);
      }
    }

    return result;
  }

  getAllMessages() {
    const allLocales = new Set();

    for (const moduleLocales of this.moduleLocales.values()) {
      Object.keys(moduleLocales).forEach(locale => allLocales.add(locale));
    }

    const messages = {};

    for (const locale of allLocales) {
      if (this.supportedLocales && !this.supportedLocales.includes(locale)) {
        continue;
      }
      messages[locale] = this.getMessages(locale);
    }

    return messages;
  }

  isValidLocales(obj) {
    return this.isObject(obj) && Object.values(obj).every(this.isObject);
  }

  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
}

export const i18nManager = new I18nManager();
export default I18nManager;
